import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { User, Save, CheckCircle, UserX, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfileTab = () => {
  const { user, updateProfile, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    company_name: user?.company_name || '',
    phone: user?.phone || ''
  });
  const [saving, setSaving] = useState(false);
  const [notif, setNotif] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile(profileForm);
      setNotif('success');
      setTimeout(() => setNotif(null), 3000);
    } catch {
      setNotif('error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/user/account`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        await logout();
        navigate('/');
      }
    } catch {
      /* connection error */
    } finally {
      setDeleteLoading(false);
      setDeleteConfirm(false);
    }
  };

  return (
    <div data-testid="profile-tab" className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 max-w-2xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-[#1a237e] rounded-full"><User className="w-6 h-6 text-white" /></div>
        <div>
          <h2 className="text-xl font-bold text-[#0d1442]">{t('dashboard.profileTitle')}</h2>
          <p className="text-sm text-slate-500">{t('dashboard.profileSubtitle')}</p>
        </div>
      </div>

      {notif === 'success' && (
        <div className="flex items-center gap-2 p-3 mb-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          <CheckCircle className="w-4 h-4" />{t('dashboard.profileSaved')}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">{t('auth.email')}</label>
          <input data-testid="profile-email" type="email" value={user?.email || ''} disabled className="w-full px-4 py-3 border border-slate-200 rounded-lg bg-slate-50 text-slate-500 cursor-not-allowed" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">{t('auth.name')}</label>
          <input data-testid="profile-name" type="text" value={profileForm.name} onChange={e => setProfileForm(p => ({...p, name: e.target.value}))} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a237e]" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">{t('auth.companyName')}</label>
          <input data-testid="profile-company" type="text" value={profileForm.company_name} onChange={e => setProfileForm(p => ({...p, company_name: e.target.value}))} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a237e]" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">{t('auth.phone')}</label>
          <input data-testid="profile-phone" type="tel" value={profileForm.phone} onChange={e => setProfileForm(p => ({...p, phone: e.target.value}))} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a237e]" />
        </div>
        <button data-testid="profile-save-btn" onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-[#1a237e] text-white font-medium rounded-lg hover:bg-[#0d1442] transition-colors disabled:opacity-50">
          <Save className="w-4 h-4" />
          {saving ? '...' : t('dashboard.saveProfile')}
        </button>
      </div>

      {/* Delete Account */}
      {user?.role !== 'admin' && (
        <div className="mt-10 pt-8 border-t border-red-100">
          <h3 className="text-base font-semibold text-red-600 mb-2">{t('dashboard.deleteAccountTitle')}</h3>
          <p className="text-sm text-slate-500 mb-4">{t('dashboard.deleteAccountWarning')}</p>
          {!deleteConfirm ? (
            <button data-testid="delete-account-btn" onClick={() => setDeleteConfirm(true)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors">
              <UserX className="w-4 h-4" />{t('dashboard.deleteAccount')}
            </button>
          ) : (
            <div data-testid="delete-confirm-section" className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm font-medium text-red-700 mb-3">{t('dashboard.deleteConfirm')}</p>
              <div className="flex gap-3">
                <button data-testid="delete-account-confirm-btn" onClick={handleDelete} disabled={deleteLoading}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50">
                  <Trash2 className="w-4 h-4" />{deleteLoading ? '...' : t('dashboard.deleteConfirmYes')}
                </button>
                <button data-testid="delete-account-cancel-btn" onClick={() => setDeleteConfirm(false)}
                  className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors">
                  {t('dashboard.deleteConfirmNo')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileTab;
